import { useEffect } from 'react';
import type { TrackState } from '@melodinai/types';
import { useMixerStore } from '../../stores/mixerStore';
import { SimpleMacros } from './SimpleMacros';
import { SslEqSection } from './SslEqSection';
import { SslCompressorSection } from './SslCompressorSection';
import { SaturationSection } from './SaturationSection';
import { SendsSection } from './SendsSection';
import { AiMixAssistant } from './AiMixAssistant';
import { SslMeter } from './meters/SslMeter';

interface ChannelInspectorProps {
  track: TrackState | null;
}

export function ChannelInspector({ track }: ChannelInspectorProps) {
  const mode = useMixerStore((s) => s.mode);
  const meters = useMixerStore((s) => s.meters);
  const ensureChannelStrip = useMixerStore((s) => s.ensureChannelStrip);
  const setInputGain = useMixerStore((s) => s.setInputGain);
  const setPhaseInvert = useMixerStore((s) => s.setPhaseInvert);
  const setLowCut = useMixerStore((s) => s.setLowCut);
  const setPan = useMixerStore((s) => s.setPan);
  const setMacro = useMixerStore((s) => s.setMacro);
  const setFxParam = useMixerStore((s) => s.setFxParam);
  const setVolume = useMixerStore((s) => s.setVolume);

  useEffect(() => {
    if (track?.id) ensureChannelStrip(track.id);
  }, [track?.id, ensureChannelStrip]);

  if (!track) {
    return (
      <div className="flex h-full items-center justify-center p-4 text-center text-xs text-ssl-muted">
        Select a track to open the SSL channel strip
      </div>
    );
  }

  const m = meters[track.id];
  const macros = track.macros || { punch: 0, weight: 0, air: 0, width: 0.5 };
  const util = track.fxChain?.find((f) => f.type === 'Utility');
  const width = Number((util?.params as Record<string, number> | undefined)?.width ?? 1);

  return (
    <div className="flex h-full flex-col bg-ssl-panel text-ssl-text">
      <header className="shrink-0 border-b border-ssl-metal/50 px-3 py-2">
        <div className="truncate text-sm font-semibold tracking-wide">{track.name}</div>
        <div className="text-[9px] uppercase tracking-wider text-ssl-muted">
          {track.type || 'audio'} · {mode === 'simple' ? 'Simple' : 'Studio'}
        </div>
      </header>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-3 py-3">
        {mode === 'simple' ? (
          <>
            <section className="space-y-2">
              <h3 className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ssl-accent">Musical Macros</h3>
              <SimpleMacros macros={macros} onChange={(k, v) => setMacro(track.id, k, v)} />
            </section>
            <label className="flex items-center gap-2 text-[9px] text-ssl-muted">
              <span className="w-12">Volume</span>
              <input
                type="range"
                min={0}
                max={1.5}
                step={0.01}
                value={track.volume}
                onChange={(e) => setVolume(track.id, Number(e.target.value))}
                className="h-1 flex-1 accent-ssl-fader"
              />
            </label>
            <AiMixAssistant track={track} />
          </>
        ) : (
          <>
            <section className="space-y-2 border-b border-ssl-metal/50 pb-3">
              <h3 className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ssl-accent">Input</h3>
              <label className="flex items-center gap-2 text-[9px] text-ssl-muted">
                <span className="w-14">Gain</span>
                <input
                  type="range"
                  min={0}
                  max={2}
                  step={0.01}
                  value={track.inputGain}
                  onChange={(e) => setInputGain(track.id, Number(e.target.value))}
                  className="h-1 flex-1 accent-ssl-accent"
                />
              </label>
              <label className="flex items-center gap-2 text-[9px] text-ssl-muted">
                <input
                  type="checkbox"
                  checked={!!track.phaseInvert}
                  onChange={(e) => setPhaseInvert(track.id, e.target.checked)}
                />
                Phase Invert
              </label>
              <label className="flex items-center gap-2 text-[9px] text-ssl-muted">
                <span className="w-14">Low Cut</span>
                <input
                  type="range"
                  min={20}
                  max={500}
                  step={1}
                  value={track.lowCutHz ?? 20}
                  onChange={(e) => setLowCut(track.id, Number(e.target.value))}
                  className="h-1 flex-1 accent-ssl-led"
                />
                <span className="w-12 tabular-nums text-right">{Math.round(track.lowCutHz ?? 20)} Hz</span>
              </label>
            </section>

            <SslEqSection track={track} />
            <SslCompressorSection track={track} />
            <SaturationSection track={track} />

            <section className="space-y-2 border-b border-ssl-metal/50 pb-3">
              <h3 className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ssl-accent">Pan / Width</h3>
              <label className="flex items-center gap-2 text-[9px] text-ssl-muted">
                <span className="w-12">Pan</span>
                <input
                  type="range"
                  min={-1}
                  max={1}
                  step={0.01}
                  value={track.pan}
                  onChange={(e) => setPan(track.id, Number(e.target.value))}
                  className="h-1 flex-1 accent-ssl-accent"
                />
              </label>
              <label className="flex items-center gap-2 text-[9px] text-ssl-muted">
                <span className="w-12">Width</span>
                <input
                  type="range"
                  min={0}
                  max={2}
                  step={0.01}
                  value={width}
                  onChange={(e) => setFxParam(track.id, 'Utility', 'width', Number(e.target.value))}
                  className="h-1 flex-1 accent-ssl-led"
                />
              </label>
            </section>

            <SendsSection track={track} />

            <section className="space-y-2">
              <h3 className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ssl-accent">Output</h3>
              <div className="flex items-center gap-3">
                <SslMeter
                  peakL={m?.peakL}
                  peakR={m?.peakR}
                  rmsL={m?.rmsL}
                  rmsR={m?.rmsR}
                  grDb={m?.grDb}
                  height={80}
                />
                <label className="flex flex-1 flex-col gap-1 text-[9px] text-ssl-muted">
                  Fader
                  <input
                    type="range"
                    min={0}
                    max={1.5}
                    step={0.01}
                    value={track.volume}
                    onChange={(e) => setVolume(track.id, Number(e.target.value))}
                    className="h-1 w-full accent-ssl-fader"
                  />
                </label>
              </div>
            </section>

            <AiMixAssistant track={track} />
          </>
        )}
      </div>
    </div>
  );
}
